import {
  Box,
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import Big from 'big.js';
import { isEmpty, kebabCase } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { InferType } from 'yup';

import { getMakgeolliFactorsGreenhouseGases } from '@/apis/makgeolli/factors/greenhouse-gases';
import { defaultCustomRefrigerant, RefrigerantComposition } from '@/apis/makgeolli/factors/greenhouse-gases.dto';
import {
  REFRIGERANT_CUSTOM_KEYS,
  REFRIGERANT_OTHERS_OPTION,
} from '@/app/org-cfv/[projectId]/emission-sources/constants';
import { IPCC } from '@/app/org-cfv/src/enums/CreateProjectEnum';
import { FormMode } from '@/app/org-cfv/src/enums/EmissionSourceEnum';
import { RefrigerantGHG } from '@/app/org-cfv/src/enums/FugitiveEmissionEnum';
import TableEmptyDataPlaceholder from '@/components/common/DataDisplay/table/TableEmptyDataPlaceholder';
import { DropdownOption } from '@/components/common/Forms/inputs/DropdownInput/typings';
import SearchDropdownInput from '@/components/common/Forms/inputs/SearchDropdownInput/SearchDropdownInput';
import { ModalTypes } from '@/components/common/Modals/modals/typings';
import useFetcher from '@/hooks/useFetcher';
import useModal from '@/hooks/useModal';
import { NextTFunction, useNextTranslation } from '@/i18n/client';
import { useProjectStore } from '@/stores/org-cfv/useProjectStore';
import { isPositiveNumberOrZero } from '@/utils/validations';
import yup from '@/utils/yup';

import RefrigerantBuiltInBlock from './RefrigerantBuiltInBlock';
import RefrigerantCustomBlock from './RefrigerantCustomBlock';

const isCustom = (value: string): boolean => {
  return REFRIGERANT_CUSTOM_KEYS.includes(value as RefrigerantGHG);
};

type Props = {
  isOpen: boolean;
  mode: FormMode;
  refrigerantErrorMessage: string;
  defaultData?: RefrigerantComposition;
  onConfirm: (data: RefrigerantComposition) => void;
  onClose: () => void;
};
const RefrigerantSettingModal = ({ isOpen, refrigerantErrorMessage, mode, defaultData, onConfirm, onClose }: Props) => {
  const { t } = useNextTranslation();
  const { setConfirmModal, setUnsavedChangesModal } = useModal();
  const { project } = useProjectStore();
  const arVersion = project?.projectVerificationEntity?.ipcc || IPCC.NONE;
  const schema = getRefrigerantSettingSchema(t);
  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(schema),
  });
  const {
    handleSubmit,
    reset: setCurRefrigerant,
    watch,
    formState: { isDirty },
    getValues,
  } = methods;
  const curRefrigerant = watch();

  const { trigger: triggerGetGhgList, data, isMutating } = useFetcher(getMakgeolliFactorsGreenhouseGases);
  const ghgResponseList = useMemo(() => data?.data.ghgs.concat(defaultCustomRefrigerant) || [], [data]);
  const totalRatio =
    getValues('gasComposition')
      ?.reduce((total, item) => {
        return total.plus(new Big(item.ratio || 0));
      }, new Big(0))
      .toNumber() || 0;
  useEffect(() => {
    triggerGetGhgList({
      query: {
        arVersion,
      },
    });
  }, [arVersion, triggerGetGhgList]);

  useEffect(() => {
    if (defaultData) {
      const target = defaultData;
      target.shortName = target.shortName || target.chemicalFormulaName;

      if (!target.gasComposition) {
        target.gasComposition = [
          {
            chemicalFormulaName: target.chemicalFormulaName,
            shortName: target.shortName || target.chemicalFormulaName,
            isMontrealRegulated: target.isMontrealRegulated,
            isIncluded: !target.isMontrealRegulated,
            ratio: 100,
          },
        ];
      }

      setCurRefrigerant(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ghgOptions: DropdownOption[] = ghgResponseList
    .filter(
      (ghg) =>
        ![
          RefrigerantGHG.GHG_CUSTOM_2,
          RefrigerantGHG.GHG_CUSTOM_3,
          RefrigerantGHG.GHG_CUSTOM_4,
          RefrigerantGHG.GHG_CUSTOM_5,
        ].includes(ghg.key),
    )
    .map((ghg) => ({
      value: ghg.key,
      label: ghg.key === REFRIGERANT_OTHERS_OPTION ? t('common.other') : t(`fugitive-emission.${ghg.key}`),
    }));

  const handleRefrigerantChange = (value: string) => {
    const foundRefrigerant = ghgResponseList.find((ghg) => ghg.key === value);
    if (!foundRefrigerant) {
      return;
    }
    const { key, ...target } = foundRefrigerant;
    if (target.gasComposition) {
      target.gasComposition = target.gasComposition.map((item) => ({
        ...item,
        isIncluded: !item.isMontrealRegulated,
        shortName: item.shortName || item.chemicalFormulaName,
      }));
    } else {
      target.shortName = target.shortName || target.chemicalFormulaName;
      target.gasComposition = [
        {
          chemicalFormulaName: target.chemicalFormulaName,
          shortName: target.shortName || target.chemicalFormulaName,
          isMontrealRegulated: target.isMontrealRegulated,
          isIncluded: !target.isMontrealRegulated,
          ratio: 100,
        },
      ];
    }

    setCurRefrigerant({
      ...target,
      refrigerant: key,
    });
  };

  const transferToOutputData = (original: RefrigerantSettingSchemaType): RefrigerantComposition => {
    const result = original as RefrigerantComposition;
    if (isCustom(original.refrigerant)) {
      result.chemicalFormulaName = original.shortName;
      result.gwp = original.gwp ? +original.gwp : 0;
    }

    result.gasComposition = original.gasComposition?.map((item) => ({
      ...item,
      chemicalFormulaName: item.shortName,
      ratio: item.ratio ? +item.ratio : 0,
    }));
    return result;
  };

  const handleConfirmData = (data: RefrigerantSettingSchemaType) => {
    const outputData = transferToOutputData(data);
    onConfirm(outputData);
  };

  const checkRatioIs100 = (_: RefrigerantSettingSchemaType, nextStep: () => void) => {
    if (totalRatio === 100) {
      nextStep();
      return;
    }
    setConfirmModal({
      type: ModalTypes.WARNING,
      title: t('common.commonAlertTitle_1'),
      description: t('common.refrigerantWarningMsg'),
      onConfirm: () => {
        nextStep();
      },
    });
  };

  const handleAddAnotherRefrigerant = handleSubmit((data) => {
    const nextStep = () => {
      handleConfirmData(data);
      setCurRefrigerant({ refrigerant: '' });
    };

    if (isCustom(data.refrigerant)) {
      checkRatioIs100(data, nextStep);
      return;
    }

    nextStep();
  });

  const handleClickConfirm = handleSubmit((data) => {
    const nextStep = () => {
      handleConfirmData(data);
      setCurRefrigerant({ refrigerant: '' });
      onClose();
    };
    if (isCustom(data.refrigerant)) {
      checkRatioIs100(data, nextStep);
      return;
    }
    nextStep();
  });

  const handleClose = () => {
    if (isDirty) {
      setUnsavedChangesModal({
        onConfirm: () => {
          onClose();
          setCurRefrigerant({ refrigerant: '' });
        },
        backOnConfirmed: false,
      });
    } else {
      onClose();
      setCurRefrigerant({ refrigerant: '' });
    }
  };
  const title = (function () {
    switch (mode) {
      case FormMode.CREATE:
        return t('common.addRefrigerant');
      case FormMode.EDIT:
        return t('common.editRefrigerant');
      case FormMode.VIEW:
      default:
        return t('common.view');
    }
  })();

  return (
    <Modal isOpen={isOpen} onClose={handleClose} scrollBehavior='inside' closeOnOverlayClick={!isMutating}>
      <ModalOverlay />
      <ModalContent maxW='75rem' minW='30rem' maxH='calc(100vh - 3rem)'>
        <ModalHeader boxShadow='md'>{title}</ModalHeader>
        <ModalCloseButton data-testid='close_btn' isDisabled={isMutating} />
        <ModalBody px={6} py={4} minH={395}>
          {[FormMode.EDIT, FormMode.CREATE].includes(mode) && (
            <Flex mb={4} alignItems={'center'} gap={2}>
              <SearchDropdownInput
                placeholder={t('common.searchForKeyword')}
                options={ghgOptions}
                value={[] as typeof ghgOptions}
                testId={'search-refrigerant-dropdown'}
                width={'50%'}
                onChange={(option) => option && handleRefrigerantChange(option.value)}
              />
              <Link
                color='blue.500'
                fontSize={'sm'}
                data-testid='custom-refrigerant-link'
                onClick={() => {
                  handleRefrigerantChange(RefrigerantGHG.GHG_CUSTOM_1);
                }}
              >
                {t('common.customRefrigerantOptions')}
              </Link>
            </Flex>
          )}
          {refrigerantErrorMessage && (
            <Text color='red.500' my={4} fontSize={'sm'}>
              {refrigerantErrorMessage}
            </Text>
          )}
          {isEmpty(curRefrigerant.refrigerant) ? (
            <Box
              border='1px solid'
              borderColor='gray.100'
              borderRadius='8px'
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              height={320}
            >
              <TableEmptyDataPlaceholder placeholderText={t('common.noDataAvailable')} />
            </Box>
          ) : isCustom(curRefrigerant.refrigerant) ? (
            <RefrigerantCustomBlock methods={methods} mode={mode} totalRatio={totalRatio} />
          ) : (
            <RefrigerantBuiltInBlock methods={methods} mode={mode} />
          )}
        </ModalBody>
        <ModalFooter justifyContent='space-between' boxShadow='0 -4px 20px -12px rgba(0, 0, 0, 0.25)'>
          <Button
            type='button'
            colorScheme={mode === FormMode.VIEW ? 'blue' : 'gray'}
            variant={mode === FormMode.VIEW ? 'solid' : 'outline'}
            onClick={handleClose}
            data-testid='cancel-btn'
            isDisabled={isMutating}
            ml={mode === FormMode.VIEW ? 'auto' : undefined}
          >
            {mode === FormMode.VIEW ? t('common.close') : t('common.cancel')}
          </Button>
          {[FormMode.CREATE, FormMode.EDIT].includes(mode) && (
            <Box>
              {mode === FormMode.CREATE && (
                <Button
                  colorScheme='blue'
                  size='md'
                  mr={4}
                  variant='outline'
                  onClick={handleAddAnotherRefrigerant}
                  data-testid={kebabCase('addAnotherRefrigerant')}
                  isDisabled={isMutating}
                >
                  {t('common.addAnotherRefrigerant')}
                </Button>
              )}

              <Button
                colorScheme='blue'
                size='md'
                onClick={curRefrigerant.refrigerant ? handleClickConfirm : handleClose}
                data-testid='next-btn'
                isLoading={isMutating}
              >
                {t('common.confirm')}
              </Button>
            </Box>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RefrigerantSettingModal;

export const getRefrigerantSettingSchema = (t: NextTFunction) => {
  return yup.object({
    refrigerant: yup.string().required(),
    shortName: yup.string().required(t('common.required')),
    gwp: yup.number().when('refrigerant', {
      is: (val: string) => isCustom(val),
      then: (schema) =>
        schema
          .required(t('common.required'))
          .transform((value: string, originalValue: string) =>
            typeof originalValue === 'string' && originalValue.trim() === '' ? -1 : value,
          )
          .test({
            name: 'isGWPFilled',
            message: t('error-message.greaterOrEqualToANumber', { number: 0 }),
            test: (value) => {
              return isPositiveNumberOrZero(value);
            },
          }),
    }),
    gasComposition: yup.array().of(
      yup.object({
        shortName: yup.string().required(t('common.required')),
        ratio: yup
          .number()
          .required(t('common.required'))
          .transform((value: string, originalValue: string) =>
            typeof originalValue === 'string' && originalValue.trim() === '' ? -1 : value,
          )
          .test({
            name: 'isRatioFilled',
            message: t('error-message.greaterOrEqualToANumber', { number: 0 }),
            test: (value) => {
              return isPositiveNumberOrZero(value);
            },
          }),
        isIncluded: yup.boolean().required(t('common.required')),
        isMontrealRegulated: yup.boolean(),
      }),
    ),
  });
};

type schemaForType = ReturnType<typeof getRefrigerantSettingSchema>;
export type RefrigerantSettingSchemaType = InferType<schemaForType>;
